import React, { createContext, useContext, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Template } from "models/templates/template";

const TemplateContext = createContext();

export const SelectedOptionProvider = ({ children }) => {
	const [templateTypeSelection, setTemplateTypeSelection] = useState({});

	// State for the selected tab by block
	// This is used to display the correct tab for each block
	const [activeTabByBlock, setActiveTabByBlock] = useState({});

	const [informationsToDisplay, setInformationsToDisplay] = useState({
		customTemplateFieldDefinition: { name: "custom", items: [] },
		defaultTemplateFieldDefinition: { name: "default", items: [] },
		mapping: { name: "mapping", items: [] }
	});

	/**
	 * Creates a standardized block object from raw block data
	 * @param {string} key - The block identifier/key
	 * @param {Object} block - The raw block data from Template instance
	 * @returns {Object} Formatted block object with:
	 *   - title: the block key
	 *   - open: boolean if block is expanded
	 *   - showDelete: boolean if delete option should be shown
	 *   - rows: formatted row data
	 */
	const createBlockObject = (key, block) => ({
		title: key,
		open: true,
		showDelete: false,
		rows: Object.entries(block.getRows()).map(([rowKey, row]) => ({
			[rowKey]: row.getItems()
		}))
	});

	/**
	 * Parses raw template data into an array of formatted blocks
	 * @param {Object} data - Raw template mapping data
	 * @returns {Array} Array of parsed block objects
	 * @throws Will log error to console if parsing fails
	 */
	const parseBlocks = useCallback(data => {
		try {
			const template = new Template({ mapping: data });
			const blocksMapping = template.getMapping().getBlocks();
			if (!blocksMapping) return [];

			return Object.entries(blocksMapping).map(([key, block]) =>
				createBlockObject(key, block)
			);
		} catch (error) {
			console.error("Error parsing blocks:", error);
			return [];
		}
	}, []);

	/**
	 * Checks if a block needs to be reset by looking for non-null values
	 * @param {Object} items - The block items to check
	 * @returns {boolean} True if at least one item has a non-null value
	 */
	const needsReset = items => {
		return Object.values(items).some(
			row =>
				row &&
				typeof row === "object" &&
				Object.values(row).some(item => item?.value !== null)
		);
	};

	/**
	 * Resets all values in a block items object to null
	 * @param {Object} items - The items to reset
	 * @returns {Object} New items object with all values set to null
	 */
	const resetBlockItems = items => {
		return Object.fromEntries(
			Object.entries(items).map(([rowKey, row]) => {
				if (!row || typeof row !== "object") return [rowKey, row];

				return [
					rowKey,
					Object.fromEntries(
						Object.entries(row).map(([itemKey, item]) => [
							itemKey,
							item?.value !== undefined
								? { ...item, value: null }
								: item
						])
					)
				];
			})
		);
	};

	/**
	 * Resets all values in a specific block if needed
	 * @param {string} blockName - Name of the block to reset
	 */
	const resetBlockValues = React.useCallback(
		blockName => {
			setInformationsToDisplay(prev => {
				if (!prev?.mapping?.items?.[blockName]) return prev;

				if (!needsReset(prev.mapping.items[blockName])) return prev;

				return {
					...prev,
					mapping: {
						...prev.mapping,
						items: {
							...prev.mapping.items,
							[blockName]: resetBlockItems(
								prev.mapping.items[blockName]
							)
						}
					}
				};
			});
		},
		[setInformationsToDisplay]
	);

	const handleTabChange = (blockName, tabIndex) => {
		const currentTab = activeTabByBlock[blockName]?.tabIndex ?? 0;
		if (currentTab === tabIndex) return;

		setActiveTabByBlock(prev => ({
			...prev,
			[blockName]: {
				tabIndex,
				isImage: tabIndex === 1
			}
		}));

		// Reset only here ( we we change betwwen tabs)
		resetBlockValues(blockName);
	};

	const updateActiveTab = useCallback(
		(BlockName, tabIndex) => {
			if (typeof BlockName !== "string") {
				return setActiveTabByBlock(prev => ({
					...prev,
					...BlockName
				}));
			}

			const prevTabIndex = activeTabByBlock[BlockName]?.tabIndex ?? 0;
			if (prevTabIndex !== tabIndex) {
				resetBlockValues(BlockName);
			}

			setActiveTabByBlock(prev => ({
				...prev,
				[BlockName]: {
					tabIndex,
					isImage: tabIndex === 1
				}
			}));
		},
		[activeTabByBlock, resetBlockValues]
	);

	return (
		<TemplateContext.Provider
			value={{
				templateTypeSelection,
				setTemplateTypeSelection,
				informationsToDisplay,
				setInformationsToDisplay,
				parseBlocks,
				updateActiveTab,
				activeTabByBlock,
				setActiveTabByBlock,
				handleTabChange,
				resetBlockValues
			}}
		>
			{children}
		</TemplateContext.Provider>
	);
};

SelectedOptionProvider.propTypes = {
	children: PropTypes.node.isRequired
};

export const useTemplateContext = () => useContext(TemplateContext);
